import React, {useEffect, useState} from 'react';
import {
  createCompanyGroupAdmin,
  getCurrencies,
  type CurrencyOption,
  uploadCompanyGroupBrandingAsset,
} from '../core/_requests';
import {toast} from 'react-toastify';
import {KTSVG} from '../../../../_metronic/helpers';
import CurrencySelect from './CurrencySelect';
import {getApiErrorMessage, showErrorToast} from '../../../shared/utils/apiErrorMessage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCompanyAdded: (loginInfo: {companyId: number; username: string; password: string}) => void;
}

const AddCompanyAdminModal: React.FC<Props> = ({onClose, isOpen, onCompanyAdded}) => {
  const [companyName, setCompanyName] = useState('');
  const [contact, setContact] = useState('');
  const [altContact, setAltContact] = useState('');
  const [email, setEmail] = useState('');
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [landmark, setLandmark] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [companyDetails, setCompanyDetails] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [primaryCurrencyCode, setPrimaryCurrencyCode] = useState('INR');
  const [currencies, setCurrencies] = useState<CurrencyOption[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [watermarkFile, setWatermarkFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      clearData();
    }
  }, [isOpen]);

  useEffect(() => {
    void (async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        setCurrencies(await getCurrencies());
      } catch (loadError: any) {
        console.error('Failed to load currencies:', loadError);
        setCurrencies([]);
        setCurrenciesError(getApiErrorMessage(loadError, 'Unable to load active currencies.', 'load'));
      } finally {
        setCurrenciesLoading(false);
      }
    })();
  }, []);

  const isImageFile = (file: File) => Boolean(file.type) && file.type.toLowerCase().startsWith('image/');

  const validateBrandingFile = (file: File | null, label: string) => {
    if (!file) return true;
    if (!isImageFile(file)) {
      toast.error(`${label} must be an image file.`, {position: 'top-center'});
      return false;
    }
    return true;
  };

  const validateFields = () => {
    const errors: Record<string, string> = {};

    if (!companyName.trim()) errors.companyName = 'Company Name is required.';
    if (!contact.trim()) {
      errors.contact = 'Contact Number is required.';
    } else if (!/^\+?[0-9\s\-()]{7,20}$/.test(contact)) {
      errors.contact = 'Enter a valid international phone number.';
    }

    if (altContact && !/^\+?[0-9\s\-()]{7,20}$/.test(altContact)) {
      errors.altContact = 'Enter a valid international phone number.';
    }

    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(email)) {
      errors.email = 'Invalid email format.';
    }

    if (!addr1.trim()) errors.addr1 = 'Address Line 1 is required.';
    if (!country.trim()) errors.country = 'Country is required.';
    if (!state.trim()) errors.state = 'State is required.';

    if (gstNumber.trim()) {
      const cleanGst = gstNumber.trim().toUpperCase();
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(cleanGst)) {
        errors.gstNumber = 'Invalid GSTIN format (e.g. 22AAAAA0000A1Z5).';
      }
    }

    if (panNumber.trim()) {
      const cleanPan = panNumber.trim().toUpperCase();
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanPan)) {
        errors.panNumber = 'Invalid PAN format (e.g. ABCDE1234F).';
      }
    }

    if (vatNumber.trim()) {
      const cleanVat = vatNumber.trim().toUpperCase();
      if (!/^[A-Z0-9]{8,15}$/.test(cleanVat)) {
        errors.vatNumber = 'Invalid VAT format (8-15 alphanumeric characters).';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateUsername = (company: string) => {
    return (
      company.trim().toLowerCase().replace(/\s+/g, '_') + Math.floor(100 + Math.random() * 900)
    );
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$!';
    return Array.from({length: 10}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const clearData = () => {
    setCompanyName('');
    setContact('');
    setAltContact('');
    setEmail('');
    setAddr1('');
    setAddr2('');
    setLandmark('');
    setCountry('');
    setState('');
    setCity('');
    setCompanyDetails('');
    setGstNumber('');
    setPanNumber('');
    setVatNumber('');
    setPrimaryCurrencyCode('INR');
    setLogoFile(null);
    setWatermarkFile(null);
    setFieldErrors({});
    setError(null);
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!validateBrandingFile(file, 'Company logo')) {
      event.target.value = '';
      setLogoFile(null);
      return;
    }
    setLogoFile(file);
  };

  const handleWatermarkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!validateBrandingFile(file, 'Company watermark')) {
      event.target.value = '';
      setWatermarkFile(null);
      return;
    }
    setWatermarkFile(file);
  };

  const handleAddCompany = async () => {
    if (!validateFields()) return;
    if (
      !validateBrandingFile(logoFile, 'Company logo') ||
      !validateBrandingFile(watermarkFile, 'Company watermark')
    ) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const userName = generateUsername(companyName);
      const password = generatePassword();
      const response = await createCompanyGroupAdmin(
        companyName,
        userName,
        password,
        contact,
        altContact,
        email,
        addr1,
        addr2,
        landmark,
        country,
        state,
        city,
        companyDetails,
        primaryCurrencyCode,
        gstNumber,
        panNumber,
        vatNumber
      );

      const companyId = response?.id;
      if (!companyId) {
        toast.error('Company ID not returned', {position: 'top-center'});
        return;
      }

      const uploadWarnings: string[] = [];
      if (logoFile) {
        try {
          await uploadCompanyGroupBrandingAsset(companyId, 'logo', logoFile);
        } catch (uploadError: any) {
          console.error('Failed to upload company logo:', uploadError);
          uploadWarnings.push(uploadError?.message || 'Logo upload failed.');
        }
      }

      if (watermarkFile) {
        try {
          await uploadCompanyGroupBrandingAsset(companyId, 'watermark', watermarkFile);
        } catch (uploadError: any) {
          console.error('Failed to upload company watermark:', uploadError);
          uploadWarnings.push(uploadError?.message || 'Watermark upload failed.');
        }
      }

      onCompanyAdded({companyId, username: userName, password});
      if (uploadWarnings.length > 0) {
        toast.warning(`Company added, but branding upload issues: ${uploadWarnings.join(' ')}`, {
          position: 'top-center',
        });
      } else {
        toast.success('Company added successfully');
      }
      clearData();
      onClose();
    } catch (err: any) {
      console.error('Failed to add company:', err);
      const message = getApiErrorMessage(err, 'Failed to add company.', 'create');
      setError(message);
      toast.error(message, {position: 'top-center'});
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='modal-overlay'>
      <div className='modal-content' onClick={(e) => e.stopPropagation()} style={{maxWidth: '60rem'}}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>Add Company</h5>
          <button className='close-btn' onClick={onClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className='custom-modal-body'>
          {error && <div className='alert alert-danger'>{error}</div>}

          <div className='row'>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Primary Currency <span className='text-danger'>*</span>
              </label>
              <CurrencySelect
                ariaLabel='Primary Currency'
                currencies={currencies}
                loading={currenciesLoading}
                error={currenciesError}
                value={primaryCurrencyCode}
                onChange={setPrimaryCurrencyCode}
                required
              />
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Company Name <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              {fieldErrors.companyName && <small className='text-danger'>{fieldErrors.companyName}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Contact No. <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />
              {fieldErrors.contact && <small className='text-danger'>{fieldErrors.contact}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Alternative Contact No.</label>
              <input
                type='text'
                className='form-control'
                value={altContact}
                onChange={(e) => setAltContact(e.target.value)}
              />
              {fieldErrors.altContact && <small className='text-danger'>{fieldErrors.altContact}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Email <span className='text-danger'>*</span>
              </label>
              <input
                type='email'
                className='form-control'
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
              />
              {fieldErrors.email && <small className='text-danger'>{fieldErrors.email}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Address Line 1 <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={addr1}
                onChange={(e) => setAddr1(e.target.value)}
              />
              {fieldErrors.addr1 && <small className='text-danger'>{fieldErrors.addr1}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Address Line 2</label>
              <input
                type='text'
                className='form-control'
                value={addr2}
                onChange={(e) => setAddr2(e.target.value)}
              />
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Landmark</label>
              <input
                type='text'
                className='form-control'
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
              />
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                Country <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
              {fieldErrors.country && <small className='text-danger'>{fieldErrors.country}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>
                State <span className='text-danger'>*</span>
              </label>
              <input
                type='text'
                className='form-control'
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
              {fieldErrors.state && <small className='text-danger'>{fieldErrors.state}</small>}
            </div>
            <div className='col-md-6 mb-3'>
              <label className='modal_label'>City</label>
              <input
                type='text'
                className='form-control'
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Company Details</label>
              <input
                type='text'
                className='form-control'
                value={companyDetails}
                onChange={(e) => setCompanyDetails(e.target.value)}
              />
            </div>

            <div className='col-md-4 mb-3'>
              <label className='modal_label'>GST Number (15 chars)</label>
              <input
                type='text'
                maxLength={15}
                className='form-control text-uppercase'
                placeholder='e.g. 22AAAAA0000A1Z5'
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase().slice(0, 15))}
              />
              {fieldErrors.gstNumber && <small className='text-danger'>{fieldErrors.gstNumber}</small>}
            </div>

            <div className='col-md-4 mb-3'>
              <label className='modal_label'>PAN Number (10 chars)</label>
              <input
                type='text'
                maxLength={10}
                className='form-control text-uppercase'
                placeholder='e.g. ABCDE1234F'
                value={panNumber}
                onChange={(e) => setPanNumber(e.target.value.toUpperCase().slice(0, 10))}
              />
              {fieldErrors.panNumber && <small className='text-danger'>{fieldErrors.panNumber}</small>}
            </div>

            <div className='col-md-4 mb-3'>
              <label className='modal_label'>VAT Number (8-15 chars)</label>
              <input
                type='text'
                maxLength={15}
                className='form-control text-uppercase'
                placeholder='e.g. GB123456789'
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value.toUpperCase().slice(0, 15))}
              />
              {fieldErrors.vatNumber && <small className='text-danger'>{fieldErrors.vatNumber}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Company Logo</label>
              <input type='file' accept='image/*' className='form-control' onChange={handleLogoChange} />
              {logoFile && <small className='text-muted'>{logoFile.name}</small>}
            </div>

            <div className='col-md-6 mb-3'>
              <label className='modal_label'>Company Watermark</label>
              <input
                type='file'
                accept='image/*'
                className='form-control'
                onChange={handleWatermarkChange}
              />
              {watermarkFile && <small className='text-muted'>{watermarkFile.name}</small>}
            </div>
          </div>
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <button className='btn btn_secondary' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn_success' onClick={handleAddCompany} disabled={loading}>
            {loading ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCompanyAdminModal;
